import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

export interface SegmentedTabItem {
  key: string;
  label: React.ReactNode;
  /** Optional tour anchor, forwarded to the tab button as data-tour. */
  dataTour?: string;
}

interface SegmentedTabsProps {
  items: SegmentedTabItem[];
  value: string;
  onChange: (key: string) => void;
  /** Accessible name for the tab strip. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Top-level section tabs: primary-700 underline + primary-700 text on the
 * active tab, muted gray and borderless on the rest.
 */
export default function SegmentedTabs({
  items,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedTabsProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("flex items-center gap-1 border-b border-neutral-100", className)}
    >
      {items.map((item) => {
        const active = item.key === value;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.key)}
            {...(item.dataTour ? { "data-tour": item.dataTour } : {})}
            className={cn(
              "shrink-0 whitespace-nowrap px-4 py-2.5 -mb-px text-sm transition-colors border-b-2 focus:outline-none rounded-t focus-visible:border-primary-700",
              active
                ? "border-primary-700 text-primary-700 font-semibold"
                : "border-transparent text-neutral-500 font-medium hover:text-primary-700"
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
